import { useState, useMemo } from 'react';
import { FlightSelector } from './components/FlightSelector';
import { DatePicker } from './components/DatePicker';
import { PassengerSelector } from './components/PassengerSelector';
import { BookingSummary } from './components/BookingSummary';
import { BookingButton } from './components/BookingButton';
import { mockFlights } from './utils/api';
import { Flight, Passenger, BookingStatus } from './types';
import { Rocket, Globe, Shield, Clock } from 'lucide-react';

function App() {
  // Flight state
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);

  // Date state
  const [departureDate, setDepartureDate] = useState<string>('');
  const [returnDate, setReturnDate] = useState<string>('');

  // Passenger state
  const [passengerCount, setPassengerCount] = useState(1);
  const [passengers, setPassengers] = useState<Passenger[]>([]);

  // Booking status
  const [bookingStatus, setBookingStatus] = useState<BookingStatus>('idle');
  const [confirmationId, setConfirmationId] = useState<string | null>(null);

  // Calculate total price
  const totalPrice = useMemo(() => {
    if (!selectedFlight) return 0;
    return selectedFlight.price * passengerCount;
  }, [selectedFlight, passengerCount]);

  // Update passengers when count changes
  const handlePassengerCountChange = (count: number) => {
    setPassengerCount(count);
    if (count > passengers.length) {
      // Add new passengers
      const newPassengers = [...passengers, ...Array(count - passengers.length).fill({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        passportNumber: '',
        specialNeeds: '',
      } as Passenger)];
      setPassengers(newPassengers);
    } else {
      // Remove excess passengers
      setPassengers(passengers.slice(0, count));
    }
  };

  // Update individual passenger data
  const handlePassengerUpdate = (index: number, data: Partial<Passenger>) => {
    const updated = [...passengers];
    updated[index] = { ...updated[index], ...data };
    setPassengers(updated);
  };

  // Handle successful booking
  const handleBookingSuccess = (id: string) => {
    setBookingStatus('success');
    setConfirmationId(id);
  };

  // Form validation
  const isFormValid = useMemo(() => {
    if (!selectedFlight) return false;
    if (!departureDate) return false;
    if (passengers.length === 0) return false;

    // Check all passengers have required fields
    return passengers.every(
      (p) => p.firstName && p.lastName && p.dateOfBirth && p.passportNumber
    );
  }, [selectedFlight, departureDate, passengers]);

  // Min departure date is tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-orbital-bg text-orbital-primary font-mono">
      {/* Background accent grid */}
      <div className="fixed inset-0 z-0 opacity-10 pointer-events-none"
           style={{
             backgroundImage: `linear-gradient(var(--orbital-dim) 1px, transparent 1px),
                              linear-gradient(90deg, var(--orbital-dim) 1px, transparent 1px)`,
             backgroundSize: '40px 40px'
           }} />

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <header className="mb-12 border-b border-orbital-dim pb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 border border-orbital-primary/30 rounded">
              <Rocket className="w-8 h-8 text-orbital-cyan" />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-orbital-cyan tracking-wider">
                ASTRA ORBITAL
              </h1>
              <p className="text-sm text-gray-500 mt-1 tracking-widest">
                INTERPLANETARY TRAVEL CONSOLE v2.077
              </p>
            </div>
          </div>
        </header>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column - Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Flight selector */}
            <section className="bg-orbital-card/50 border border-orbital-dim p-6">
              <FlightSelector
                flights={mockFlights}
                selectedFlight={selectedFlight}
                onSelect={setSelectedFlight}
              />
            </section>

            {/* Date pickers */}
            <section className="bg-orbital-card/50 border border-orbital-dim p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DatePicker
                  label="Departure Date"
                  value={departureDate}
                  onChange={setDepartureDate}
                  minDate={minDate}
                />
                <DatePicker
                  label="Return Date (Optional)"
                  value={returnDate || ''}
                  onChange={setReturnDate}
                  minDate={departureDate || minDate}
                />
              </div>
            </section>

            {/* Passenger selector */}
            <section className="bg-orbital-card/50 border border-orbital-dim p-6">
              <PassengerSelector
                count={passengerCount}
                onChange={handlePassengerCountChange}
                passengers={passengers}
                onPassengerUpdate={handlePassengerUpdate}
              />
            </section>
          </div>

          {/* Right column - Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              <BookingSummary
                flight={selectedFlight}
                passengers={passengers}
                departureDate={departureDate}
                returnDate={returnDate}
                totalPrice={totalPrice}
              />

              <div className="bg-orbital-card/50 border border-orbital-dim p-4 space-y-3">
                <h3 className="text-xs font-mono text-orbital-cyan uppercase tracking-widest">
                  // QUICK INFO
                </h3>
                <div className="space-y-2 text-xs text-gray-400">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orbital-magenta" />
                    <span>Fully insured orbital coverage included</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-orbital-cyan" />
                    <span>Tailored microgravity training pre-flight</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orbital-yellow" />
                    <span>24hr concierge support throughout journey</span>
                  </div>
                </div>
              </div>

              <BookingButton
                isValid={isFormValid}
                bookingData={{
                  flight: selectedFlight,
                  passengers,
                  departureDate,
                  returnDate,
                  totalPrice,
                }}
                onSuccess={handleBookingSuccess}
              />

              {/* Success confirmation card */}
              {bookingStatus === 'success' && confirmationId && (
                <div className="bg-orbital-cyan/10 border border-orbital-cyan p-4 text-center">
                  <div className="text-2xl mb-2">🎉</div>
                  <div className="text-orbital-cyan font-mono text-sm">CONFIRMATION ID</div>
                  <div className="text-lg font-mono mt-1 break-all">{confirmationId}</div>
                  <div className="mt-2 text-xs text-gray-400">
                    Save this ID. Required at check-in.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-orbital-dim text-center">
          <div className="flex justify-center gap-8 text-xs text-gray-500 font-mono mb-4">
            <span>ASTRA ORBITAL INC.</span>
            <span>|</span>
            <span>LICENSE: FAA SPACE TRANSPORT #AST-2093</span>
            <span>|</span>
            <span>SYSTEM v2.077</span>
          </div>
          <div className="text-xs text-gray-600">
            This is a demonstration interface. No actual bookings are processed.
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
